//* Libraries imports
import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

//* Components imports
import { ProcessHeader } from "./process-header";
import { ProcessTable } from "./process-table";
import { ProcessPagination } from "./process-pagination";
import { Searchbar } from "../../../../component/list/searchbar";

type ProcessListProps = {
  title: string;
  pronoun?: string;
  searchPlaceholder?: string;
  notFoundMessage: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onCreate: () => void;
  onEditProcess?: (id: number) => void;
  onDeleteProcess?: (id: number) => void;
  onViewProcess?: (id: number) => void;
  fields: Array<{
    name: string;
    header: string;
    type: "text" | "date" | "boolean";
  }>;
  items: any[];
  loading: boolean;
  hideEdit?: boolean;
  hideDelete?: boolean;
  additionalButtons?: Array<{
    label: string;
    eventHandler: (e: React.MouseEvent) => void;
  }>;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

export function ProcessList({
  title,
  pronoun = "os",
  searchPlaceholder = "Pesquisar...",
  notFoundMessage,  searchTerm,
  onSearchChange,
  onSearch,
  onCreate,
  onEditProcess,
  onDeleteProcess,
  onViewProcess,
  fields,
  items,
  loading,
  hideEdit = false,
  hideDelete = false,
  additionalButtons = [],
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
}: ProcessListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setSelectedProcessId(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedProcessId !== null && onDeleteProcess) {
      onDeleteProcess(selectedProcessId);
    }
    setShowDeleteConfirm(false);
    setSelectedProcessId(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSelectedProcessId(null);
  };

  const alertVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20, transition: { duration: 0.2 } },
  };

  return (
    <div className="col-12">
      <div className="py-5 px-3">
        <div className="card-body" id="card">
          {/* Cabeçalho */}
          <ProcessHeader title={title} pronoun={pronoun} />

          {/* Alerta de exclusão */}
          <AnimatePresence>
            {showDeleteConfirm && (
              <motion.div
                variants={alertVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="alert alert-warning mt-3"
                role="alert"
              >
                <h5>Atenção</h5>
                <p>Tem certeza que deseja excluir este processo?</p>
                <div className="d-flex gap-2">
                  <Button variant="secondary" onClick={handleDeleteCancel}>
                    Cancelar
                  </Button>
                  <Button variant="danger" onClick={handleDeleteConfirm}>
                    Confirmar
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Botões adicionais */}
          {additionalButtons.length > 0 && (
            <div className="row mb-3">
              <div className="col d-flex align-items-center flex-wrap gap-2">
                {additionalButtons.map((item) => (
                  <Button
                    key={item.label}
                    variant="success"
                    onClick={(e) => item.eventHandler(e)}
                    className="d-flex align-items-center"
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-center mb-3">
                <Searchbar 
                  search_text={searchPlaceholder}
                  search_input_value={searchTerm}
                  onChangeSearchInput={onSearchChange}
                  onSearchButtonClick={onSearch}
                  hide_btn={true}
                />
              </div>             
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <CircularProgress />
                </div>              ) : (
                <ProcessTable
                  fields={fields}
                  items={items}
                  loading={false}
                  notFoundMessage={notFoundMessage}
                  onEditProcess={onEditProcess}
                  onDeleteProcess={handleDeleteClick}
                  onViewProcess={onViewProcess}
                  hideEdit={hideEdit}
                  hideDelete={hideDelete}
                />
              )}

              {/* Pagination */}
              {onPageChange && totalPages > 1 && (
                <ProcessPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
